import React, { useCallback, useEffect, useState } from 'react';

import {
  Chip,
  EmptyState,
  ErrorState,
  ListaSelecao,
  Modal,
  Pagination,
  SearchInput,
  Skeleton,
} from '../../../ui';

import { fetchSgaRegionais } from '../../../api/sga-regional/sga-regionalApi';
import { extrairMensagemErro } from '../../../utils/apiError';

// 🔴 O CSS FICA, mesmo sem este componente usar nenhuma classe dele. `ModalRegionais.css`
// é o último definidor de `.carregando` (36 telas), `.mt-20` (10) e `.modal-busca-input`,
// e ele entra no bundle pela cadeia de imports — remover esta linha tira o estilo de
// dezenas de telas legadas, em silêncio. Mesma armadilha do `ModalVeiculos.css` (at-59).
// Sai quando a última tela legada migrar (frente D).
import './ModalRegionais.css';

/**
 * Escolha de uma regional para vincular ao usuário (reescrito na at-68 sobre o `Modal`
 * do kit, no caminho dos 9 modais de escolha da at-59).
 *
 * ⚠️ **É componente de DOMÍNIO** — conhece o espelho de regionais do SGA —, por isso
 * mora em `components/`. O que reaproveita do kit é puro.
 *
 * ## O que a reescrita mudou
 *
 *   1. **A lista é navegável pelo teclado.** Eram `<li onClick>`: `li` não recebe foco,
 *      não responde a Enter e não é anunciado como acionável — vincular uma regional era
 *      **impossível sem mouse**. A paginação era `<div onClick>`, pelo mesmo motivo.
 *   2. **Falha deixou de ser muda.** O `catch` dava um toast e deixava a lista vazia,
 *      indistinguível de "nenhuma regional cadastrada" — que pede outra ação. Agora há
 *      `ErrorState` com "Tentar novamente".
 *   3. **Fim do "Página 1 de 0"**: o `Math.ceil` saiu da tela e vive no `Pagination`.
 *   4. **Esc, clique fora e foco preso** vêm do `Modal`. O clique fora existia, por
 *      `mousedown` no document — que fecha até num arrasto começado dentro do diálogo.
 *   5. **O debounce de 400 ms** vem do `SearchInput`, em vez da cópia manual da at-11.
 *   6. **Já vinculada não re-adiciona.** Antes a linha só ficava opaca com um "✓", mas o
 *      clique continuava chamando `onSelectRegional` — e o formulário tinha de filtrar
 *      duplicata por fora.
 *
 * ⚠️ **As props seguem em inglês** (`isOpen`/`onClose`/`onSelectRegional`), como nos 9
 * modais de escolha. Traduzir é trabalho transversal da frente D, não de uma branch de
 * tela.
 */

export interface Regional {
  id: string;
  codigo_regional: string;
  nome: string;
  nome_fantasia?: string;
  cnpj?: string;
  cidade?: string;
  estado?: string;
}

interface ModalRegionaisProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectRegional: (regional: Regional) => void;
  /** Códigos já vinculados: marcados na lista e não re-selecionáveis. */
  selectedCodigos?: string[];
}

const POR_PAGINA = 10;

type Estado = 'carregando' | 'ok' | 'erro';

const ModalRegionais: React.FC<ModalRegionaisProps> = ({
  isOpen,
  onClose,
  onSelectRegional,
  selectedCodigos = [],
}) => {
  const [regionais, setRegionais] = useState<Regional[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState('');

  // Reabrir o modal não pode herdar a busca da vez anterior.
  useEffect(() => {
    if (isOpen) {
      setBusca('');
      setPagina(1);
    }
  }, [isOpen]);

  const buscar = useCallback(async () => {
    setEstado('carregando');
    try {
      const resposta = await fetchSgaRegionais(pagina, POR_PAGINA, busca);

      // Resposta sem o array é contrato quebrado, não lista vazia: tratar as duas como
      // "nenhuma regional" esconderia o problema.
      if (!resposta || !Array.isArray(resposta.items)) {
        setErro('A resposta do servidor não trouxe a lista de regionais.');
        setEstado('erro');
        return;
      }

      setRegionais(resposta.items);
      setTotal(resposta.total ?? 0);
      setEstado('ok');
    } catch (falha) {
      setErro(extrairMensagemErro(falha, 'Não foi possível carregar as regionais.'));
      setEstado('erro');
    }
  }, [pagina, busca]);

  useEffect(() => {
    if (!isOpen) return;
    void buscar();
  }, [isOpen, buscar]);

  const jaVinculadas = new Set(selectedCodigos);

  const escolher = (regional: Regional) => {
    if (jaVinculadas.has(regional.codigo_regional)) return;
    onSelectRegional(regional);
  };

  const vazio = busca ? (
    <EmptyState
      icone="busca"
      titulo="Nenhuma regional encontrada para a busca"
      descricao={`Nada corresponde a "${busca}". Tente escrever menos letras ou buscar pelo código.`}
    />
  ) : (
    <EmptyState
      icone="sem-dados"
      titulo="Nenhuma regional no espelho do SGA"
      descricao="As regionais vêm do SGA pela sincronização diária. Se a lista está vazia, rode a sincronização em Administração."
    />
  );

  return (
    <Modal
      aberto={isOpen}
      titulo="Selecionar Regional"
      descricao="A regional define quais cotas e produtos o usuário enxerga."
      tamanho="sm"
      aoFechar={onClose}
      rodape={
        estado === 'ok' && regionais.length > 0 ? (
          <Pagination
            pagina={pagina}
            totalItens={total}
            porPagina={POR_PAGINA}
            aoTrocar={setPagina}
          />
        ) : undefined
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <SearchInput
          aoBuscar={(termo) => {
            setBusca(termo);
            setPagina(1);
          }}
          placeholder="Buscar por nome ou código"
          rotuloAcessivel="Buscar regional"
        />

        {estado === 'carregando' && <Skeleton linhas={4} altura="tw-h-12" />}

        {estado === 'erro' && (
          <ErrorState
            mensagem="Não foi possível carregar as regionais."
            detalhe={erro}
            aoTentarNovamente={() => void buscar()}
          />
        )}

        {estado === 'ok' && (
          <ListaSelecao
            itens={regionais}
            chave={(r) => r.codigo_regional}
            rotulo={(r) => r.nome || 'Nome não disponível'}
            detalhe={(r) =>
              [`Código ${r.codigo_regional}`, r.cidade && r.estado ? `${r.cidade}/${r.estado}` : null]
                .filter(Boolean)
                .join(' · ')
            }
            sufixo={(r) =>
              jaVinculadas.has(r.codigo_regional) ? <Chip variante="neutro">Já vinculada</Chip> : null
            }
            escolhido={(r) => jaVinculadas.has(r.codigo_regional)}
            aoEscolher={escolher}
            vazio={vazio}
            rotuloAcessivel="Regionais disponíveis"
          />
        )}
      </div>
    </Modal>
  );
};

export default ModalRegionais;
